import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Download } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

const STATUS_BADGE = {
	paid: { variant: 'success', label: 'Paid' },
	open: { variant: 'warning', label: 'Open' },
	overdue: { variant: 'destructive', label: 'Overdue' }
};

const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

const formatAmount = (amount, currency) => {
	const symbol = CURRENCY_SYMBOLS[currency?.toLowerCase()] ?? '';
	return `${symbol}${amount.toFixed(2)}`;
};

const COMPARATORS = {
	status: (a, b) => String(a.status).localeCompare(String(b.status)),
	date: (a, b) => new Date(a.date) - new Date(b.date),
	description: (a, b) => String(a.description || a.number || '').localeCompare(String(b.description || b.number || '')),
	amount: (a, b) => Number(a.amount) - Number(b.amount)
};

/**
 * Invoice history — read-through to `GET /api/billing/invoices/{shopId}`.
 * Click a column header to sort; second click flips direction. Default sort
 * is date descending (most-recent first). No write paths: rows just link to
 * Stripe's hosted PDF / billing page.
 */
export function InvoicesTable({ invoices, isLoading }) {
	const [sort, setSort] = useState({ column: 'date', direction: 'desc' });

	const sorted = useMemo(() => {
		const cmp = COMPARATORS[sort.column] ?? COMPARATORS.date;
		const list = (invoices ?? []).slice().sort(cmp);
		return sort.direction === 'desc' ? list.reverse() : list;
	}, [invoices, sort]);

	const toggleSort = (column) => {
		setSort((prev) => {
			if (prev.column !== column) return { column, direction: column === 'date' || column === 'amount' ? 'desc' : 'asc' };
			return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
		});
	};

	if (isLoading) {
		return (
			<section>
				<SectionHeader eyebrow="History" title="Invoices" sub="Past charges from Unify." />
				<SoftCard className="space-y-3 !p-5 md:!p-6">
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
				</SoftCard>
			</section>
		);
	}

	return (
		<section>
			<SectionHeader eyebrow="History" title="Invoices" sub="Past charges from Unify." />
			<SoftCard className="!p-0">
				{sorted.length === 0 ? (
					<div className="px-5 py-6 text-sm text-muted-foreground">
						No invoices yet — you'll see one after your first billing cycle.
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead column="status" sort={sort} onToggle={toggleSort} className="pl-5">
									Status
								</SortableHead>
								<SortableHead column="date" sort={sort} onToggle={toggleSort}>
									Date
								</SortableHead>
								<SortableHead column="description" sort={sort} onToggle={toggleSort}>
									Description
								</SortableHead>
								<SortableHead column="amount" sort={sort} onToggle={toggleSort} align="right">
									Amount
								</SortableHead>
								<TableHead className="pr-5 text-right">PDF</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sorted.map((invoice) => (
								<InvoiceRow key={invoice.id} invoice={invoice} />
							))}
						</TableBody>
					</Table>
				)}
			</SoftCard>
		</section>
	);
}

function SortableHead({ column, sort, onToggle, align, className, children }) {
	const isActive = sort.column === column;
	const Icon = !isActive ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
	return (
		<TableHead className={cn(align === 'right' && 'text-right', className)}>
			<button
				type="button"
				onClick={() => onToggle(column)}
				className={cn(
					'inline-flex items-center gap-1.5 rounded-md text-inherit transition-colors hover:text-foreground',
					align === 'right' && 'ml-auto',
					isActive && 'text-foreground'
				)}
				aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
			>
				{children}
				<Icon className={cn('h-3 w-3', isActive ? 'opacity-100' : 'opacity-40')} />
			</button>
		</TableHead>
	);
}

function InvoiceRow({ invoice }) {
	const status = STATUS_BADGE[invoice.status] ?? { variant: 'secondary', label: invoice.status };
	return (
		<TableRow>
			<TableCell className="pl-5">
				<Badge variant={status.variant}>{status.label}</Badge>
			</TableCell>
			<TableCell>{format(new Date(invoice.date), 'd MMM yyyy')}</TableCell>
			<TableCell className="max-w-[260px] truncate" title={invoice.description}>
				{invoice.description || invoice.number || 'Unify subscription'}
			</TableCell>
			<TableCell className="text-right tabular-nums">{formatAmount(invoice.amount, invoice.currency)}</TableCell>
			<TableCell className="pr-5 text-right">
				{invoice.href ? (
					<a
						href={invoice.href}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
					>
						<Download className="h-3.5 w-3.5" /> PDF
					</a>
				) : (
					<span className="text-xs text-muted-foreground">—</span>
				)}
			</TableCell>
		</TableRow>
	);
}
